import React, { useMemo, useState } from "react";
import { Customer, CustomerWithBoxes } from '../../../dtos/customer';
import { Button, Link, CircularProgress, makeStyles, Chip } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { TooltipWrapper } from '../../shared/tooltip';
import { useApi } from '../../useApi';
import { Link as RouterLink } from 'react-router-dom';
import { BulkImport } from './bulkImport';
import { useOrgSiteContext } from '../../../hooks/useOrgSiteContext';
import { EnrollBiometricsDialog } from '../../shared/EnrollBiometricsDialog';
import { EnrollBiometricsButton } from '../../shared/EnrollBiometricsButton';

import { useAuth } from "../../../features/auth/context";
import { PaginationControls } from "../../PaginationControls";
import { SearchBar } from "../../SearchBar";
import { ExportButton } from "../../shared/ExportButton";
import { PageToolbar } from "../../shared/PageToolbar";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";
import { RowActionsMenu, RowAction } from "../../shared/RowActionsMenu";

import { CustomerForm } from "./CustomerForm";
import { DeleteCustomerDialog } from "./DeleteCustomerDialog";

export const CustomersPage = ({
    orgId,
    siteId,
    children,
    customers,
    setCustomers,
    page,
    itemsPerPage,
    numberOfPages,
    searchText,
    isLoading,
    importAvailable,
    addCustomer,
    editCustomer,
    setPage,
    setItemsPerPage,
    search,
}: {
    orgId?: number;
    siteId?: number;
    children?: any;
    customers: Array<Customer | CustomerWithBoxes>;
    setCustomers: (customers: Array<Customer | CustomerWithBoxes>) => void;
    page: number;
    itemsPerPage: number;
    numberOfPages: number;
    searchText: string;
    isLoading: boolean;
    importAvailable: boolean;
    addCustomer: (customer: Customer) => void;
    editCustomer: (customer: Customer) => void;
    setPage: (page: number) => void;
    setItemsPerPage: (items: number) => void;
    search: (text: string) => void;
}) => {
    const { isReadOnlyUser } = useAuth();
    const classes = useStyles();
    const { importApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [newCustomerFormIsVisible, setNewCustomerFormIsVisible] = useState(false);
    const [customerToEdit, setCustomerToEdit] = useState<Customer | undefined>();
    const [customerToEnroll, setCustomerToEnroll] = useState<Customer | undefined>(undefined);
    const [customerToDelete, setCustomerToDelete] = useState<Customer | undefined>(undefined);
    const [bulkImportVisible, setBulkImportVisible] = useState<boolean>(false);

    const {
        dealerId,
        orgId: contextOrgId,
        siteId: contextSiteId,
    } = useOrgSiteContext('customers');

    const onCustomerCreated = (newCustomer: Customer) => {
        setNewCustomerFormIsVisible(false);
        enqueueSnackbar('Customer registered!', { variant: 'success' });
        addCustomer(newCustomer);
    };

    const onCustomerEdited = (editedCustomer: Customer) => {
        setNewCustomerFormIsVisible(false);
        setCustomerToEdit(undefined);
        editCustomer(editedCustomer);
    };

    const exportRequest = useMemo(() => {
        if (!orgId) return;
        return () => importApi.exportCustomersForOrg(orgId, siteId);
    }, [orgId, siteId, importApi]);

    const organizationSelected = !!orgId;
    const siteSelected = !!siteId;

    const buildBoxLink = (boxId: number) =>
        `${dealerId ? `/dealers/${dealerId}` : ''}${orgId ? `/organizations/${contextOrgId}` : ''}${siteId ? `/sites/${contextSiteId}` : ''}/boxDetail/${boxId}`;

    const columns: Column<Customer | CustomerWithBoxes>[] = [
        { key: 'fullName', label: 'Name', primary: true },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email', hideWhenEmpty: true },
        { key: 'address', label: 'Address', hideWhenEmpty: true },
        ...(!organizationSelected ? [{ key: 'organization', label: 'Organization', hideWhenEmpty: true } as Column<Customer | CustomerWithBoxes>] : []),
        { key: 'notes', label: 'Notes', hideWhenEmpty: true },
        {
            key: 'boxes',
            label: 'Boxes',
            render: (c) => {
                const boxes = (c as CustomerWithBoxes).boxes ?? [];
                if (boxes.length === 0) return null;
                return (
                    <div className={classes.boxesChips}>
                        {boxes.map((b) => (
                            <Link
                                key={b.id}
                                component={RouterLink}
                                to={buildBoxLink(b.id)}
                                className={classes.boxChipLink}
                            >
                                <Chip size="small" label={`${b.site} - ${b.number}`} clickable />
                            </Link>
                        ))}
                    </div>
                );
            },
        },
        ...(!isReadOnlyUser ? [{
            key: 'biometrics',
            label: 'Biometrics',
            render: (c: Customer | CustomerWithBoxes) => (
                <EnrollBiometricsButton
                    user={c}
                    onSetUserIdToEnroll={(userId) => {
                        setCustomerToEnroll(customers.find(it => it.id === userId));
                    }}
                    disabled={c.isInactive || !organizationSelected}
                    compact
                />
            ),
        } as Column<Customer | CustomerWithBoxes>] : []),
    ];

    const rowActions = !isReadOnlyUser
        ? (c: Customer | CustomerWithBoxes) => {
            const menuActions: RowAction[] = [
                {
                    label: "Edit",
                    onClick: () => { setCustomerToEdit(c); setNewCustomerFormIsVisible(true); },
                    hidden: !siteId,
                },
                {
                    label: "Delete",
                    onClick: () => setCustomerToDelete(c),
                    dangerous: true,
                    disabled: !organizationSelected,
                    dividerBefore: true,
                },
            ];
            return <RowActionsMenu actions={menuActions} ariaLabel={`Actions for ${c.fullName}`} />;
        }
        : undefined;

    return (
        <>
            <div>{children}</div>
            {!newCustomerFormIsVisible ? (
                <>
                    <PageToolbar
                        search={
                            <SearchBar
                                defaultSearchText={searchText}
                                search={search}
                                labelText="Search Customers..."
                            />
                        }
                        actions={
                            <>
                                {!isReadOnlyUser && (
                                    <TooltipWrapper
                                        title={
                                            !organizationSelected
                                                ? 'You must select an organization to add a customer'
                                                : !siteSelected
                                                    ? 'You must select a site to add a customer'
                                                    : undefined
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => {
                                                    setCustomerToEdit(undefined);
                                                    setNewCustomerFormIsVisible(true);
                                                }}
                                                disabled={!organizationSelected || !siteSelected}
                                            >
                                                Register New
                                            </Button>
                                        </span>
                                    </TooltipWrapper>
                                )}
                                <TooltipWrapper
                                    title={!organizationSelected ? 'You must select an organization to import customers and box assignments' : undefined}
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setBulkImportVisible(true)}
                                            disabled={!importAvailable || !organizationSelected}
                                        >
                                            Bulk Import
                                        </Button>
                                    </span>
                                </TooltipWrapper>
                                <TooltipWrapper
                                    title={!organizationSelected ? 'You must select an organization to export customers and box assignments' : undefined}
                                >
                                    <span>
                                        <ExportButton
                                            apiRequest={exportRequest}
                                            targetLabel="Customers"
                                            buttonLabel="Bulk Export"
                                            disabled={!organizationSelected}
                                        />
                                    </span>
                                </TooltipWrapper>
                            </>
                        }
                    />

                    {bulkImportVisible && (
                        <BulkImport
                            orgId={orgId}
                            siteId={siteId}
                            closeForm={() => setBulkImportVisible(false)}
                        />
                    )}

                    <PaginationControls
                        page={page}
                        setPage={setPage}
                        pageCount={numberOfPages}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        disabled={isLoading}
                    />

                    {isLoading ? (
                        <CircularProgress />
                    ) : (
                        <ResponsiveDataView
                            rows={customers}
                            columns={columns}
                            rowKey={(c) => c.id}
                            actions={rowActions}
                            isInactive={(c) => !!c.isInactive}
                        />
                    )}
                </>
            ) : (
                <CustomerForm
                    close={() => setNewCustomerFormIsVisible(false)}
                    callback={customerToEdit ? onCustomerEdited : onCustomerCreated}
                    orgId={orgId}
                    siteId={siteId}
                    dealerId={dealerId}
                    customer={customerToEdit}
                />
            )}

            <EnrollBiometricsDialog
                key={`${customerToEnroll}`}
                user={customerToEnroll}
                orgId={orgId}
                siteId={siteId}
                onClose={() => setCustomerToEnroll(undefined)}
                enrollmentFor="customer"
            />

            {customerToDelete && (
                <DeleteCustomerDialog
                    customer={customerToDelete}
                    orgId={orgId}
                    onSuccess={(id: number) => setCustomers(customers.filter(c => c.id !== id))}
                    onClose={() => setCustomerToDelete(undefined)}
                />
            )}
        </>
    );
};

const useStyles = makeStyles({
    boxesChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        maxWidth: '320px',
    },
    boxChipLink: {
        textDecoration: 'none',
    },
});
